import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Folder,
  FolderOpen,
  FileText,
  Image,
  HardDrive,
  Upload,
  FolderPlus,
  ChevronRight,
  Trash2,
  Eye,
  Download,
  Link,
  Loader2,
  X,
  Pencil,
  Check,
  Menu,
  CheckSquare,
  Move,
  Tag,
} from 'lucide-react';
import type { DocumentFolder, EntityAttachment, CreateEntityAttachmentInput } from '@peles/shared';
import {
  useRootFolders,
  useDocumentFolders,
  useFolderBreadcrumb,
  useCreateDocumentFolder,
  useUpdateDocumentFolder,
  useDeleteDocumentFolder,
} from '../../hooks/useDocumentFolders';
import { useAttachmentsByFolder } from '../../hooks/useEntityAttachments';
import {
  useCreateEntityAttachment,
  useDeleteEntityAttachment,
  useBulkMoveEntityAttachments,
  useUpdateEntityAttachment,
} from '../../hooks/useEntityAttachments';
import { useAppSettingLists } from '../../hooks/useAppSettings';
import { getAttachmentUploadUrl } from '../../api/entity-attachments';
import { uploadFileToS3 } from '../../api/photos';
import { useGoogleDrive } from '../../hooks/useGoogleDrive';
import { useDialog } from '../../context/DialogContext';

const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
const MAX_SIZE = 50 * 1024 * 1024;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(att: EntityAttachment) {
  if (att.externalSource === 'GOOGLE_DRIVE') {
    return <HardDrive size={16} className="text-blue-500 flex-shrink-0" />;
  }
  if (att.mimeType?.startsWith('image/')) {
    return <Image size={16} className="text-blue-500 flex-shrink-0" />;
  }
  return <FileText size={16} className="text-red-500 flex-shrink-0" />;
}

function canPreview(att: EntityAttachment): boolean {
  if (att.externalSource === 'GOOGLE_DRIVE' && att.externalFileId) return true;
  if (att.downloadUrl && att.mimeType === 'application/pdf') return true;
  if (att.downloadUrl && att.mimeType?.startsWith('image/')) return true;
  return false;
}

// ─── Preview Modal ────────────────────────────────────────────────────────────

function PreviewModal({
  attachment,
  onClose,
}: {
  attachment: EntityAttachment;
  onClose: () => void;
}) {
  const isDrive = attachment.externalSource === 'GOOGLE_DRIVE';
  const isPdf = attachment.mimeType === 'application/pdf';
  const isImage = attachment.mimeType?.startsWith('image/');

  let content: React.ReactNode;
  if (isDrive && attachment.externalFileId) {
    content = (
      <iframe
        src={`https://drive.google.com/file/d/${attachment.externalFileId}/preview`}
        className="w-full h-full"
        title={attachment.fileName || 'Drive document'}
        allow="autoplay"
      />
    );
  } else if (isPdf && attachment.downloadUrl) {
    content = (
      <iframe
        src={attachment.downloadUrl}
        className="w-full h-full"
        title={attachment.fileName || ''}
      />
    );
  } else if (isImage && attachment.downloadUrl) {
    content = (
      <img
        src={attachment.downloadUrl}
        alt={attachment.fileName || ''}
        className="max-w-full max-h-full object-contain"
      />
    );
  } else {
    content = (
      <div className="text-center p-8">
        <FileText size={48} className="mx-auto mb-3 text-gray-400" />
        <p className="text-sm text-gray-600">Preview not available for this file type.</p>
        {attachment.downloadUrl && (
          <a
            href={attachment.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary-600 hover:underline mt-2 inline-block"
          >
            Download to view
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full h-full sm:h-[90vh] sm:max-w-4xl sm:mx-4 bg-white sm:rounded-lg shadow-xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {isDrive && (
              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full flex-shrink-0">
                Drive
              </span>
            )}
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {attachment.fileName || 'Document'}
            </h3>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {attachment.downloadUrl && !isDrive && (
              <a
                href={attachment.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-600 p-1"
              >
                <Download size={18} />
              </a>
            )}
            {isDrive && attachment.externalUrl && (
              <a
                href={attachment.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-600 p-1"
              >
                <Link size={18} />
              </a>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-gray-100 flex items-center justify-center">
          {content}
        </div>
      </div>
    </div>
  );
}

// ─── New Folder Modal ─────────────────────────────────────────────────────────

function NewFolderModal({ parentId, onClose }: { parentId: string | null; onClose: () => void }) {
  const [name, setName] = useState('');
  const createFolder = useCreateDocumentFolder();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    await createFolder.mutateAsync({ name: name.trim(), parentId: parentId ?? undefined });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">New Folder</h3>
        <form onSubmit={handleSubmit}>
          <input
            autoFocus
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Folder name"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 mb-4"
          />
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || createFolder.isPending}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              {createFolder.isPending && <Loader2 size={14} className="animate-spin" />}
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Folder Picker Modal ──────────────────────────────────────────────────────

function FolderPickerTreeItem({
  folder,
  depth,
  selectedId,
  excludeId,
  onSelect,
}: {
  folder: DocumentFolder;
  depth: number;
  selectedId: string | null;
  excludeId?: string;
  onSelect: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const { data: children = [] } = useDocumentFolders(expanded ? { parentId: folder.id } : {});
  const isSelected = selectedId === folder.id;
  if (folder.id === excludeId) return null;

  return (
    <div>
      <button
        onClick={() => {
          onSelect(folder.id);
          setExpanded((v) => !v);
        }}
        className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-left text-sm transition-colors ${
          isSelected
            ? 'bg-primary-50 text-primary-700 font-medium'
            : 'text-gray-700 hover:bg-gray-100'
        }`}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
      >
        {isSelected ? (
          <Check size={14} className="text-primary-600" />
        ) : expanded ? (
          <FolderOpen size={14} className="text-yellow-500" />
        ) : (
          <Folder size={14} className="text-yellow-500" />
        )}
        <span className="truncate flex-1">{folder.name}</span>
        {children.length > 0 && (
          <ChevronRight
            size={12}
            className={`flex-shrink-0 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        )}
      </button>
      {expanded &&
        children
          .filter((c) => c.id !== excludeId)
          .map((child) => (
            <FolderPickerTreeItem
              key={child.id}
              folder={child}
              depth={depth + 1}
              selectedId={selectedId}
              excludeId={excludeId}
              onSelect={onSelect}
            />
          ))}
    </div>
  );
}

function FolderPickerModal({
  count,
  excludeFolderId,
  onConfirm,
  onClose,
}: {
  count: number;
  excludeFolderId?: string;
  onConfirm: (targetFolderId: string | null) => void;
  onClose: () => void;
}) {
  const [targetFolderId, setTargetFolderId] = useState<string | null>(null);
  const { data: rootFolders = [], isLoading } = useRootFolders();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 flex flex-col max-h-[70vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">
            Move {count} file{count !== 1 ? 's' : ''} to…
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-2 px-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="animate-spin text-gray-400" />
            </div>
          ) : rootFolders.length === 0 ? (
            <p className="text-sm text-gray-400 px-2 py-4">No folders available.</p>
          ) : (
            rootFolders.map((folder) => (
              <FolderPickerTreeItem
                key={folder.id}
                folder={folder}
                depth={0}
                selectedId={targetFolderId}
                excludeId={excludeFolderId}
                onSelect={setTargetFolderId}
              />
            ))
          )}
        </div>
        <div className="flex gap-2 justify-end px-4 py-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!targetFolderId}
            onClick={() => onConfirm(targetFolderId)}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Move size={14} />
            Move here
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Folder Tree Item ─────────────────────────────────────────────────────────

function FolderTreeItem({
  folder,
  depth,
  currentFolderId,
  onSelect,
}: {
  folder: DocumentFolder;
  depth: number;
  currentFolderId: string | null;
  onSelect: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const { data: children = [] } = useDocumentFolders(expanded ? { parentId: folder.id } : {});
  const isSelected = currentFolderId === folder.id;

  return (
    <div>
      <button
        onClick={() => {
          onSelect(folder.id);
          setExpanded((v) => !v);
        }}
        className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-left text-sm transition-colors ${
          isSelected
            ? 'bg-primary-50 text-primary-700 font-medium'
            : 'text-gray-700 hover:bg-gray-100'
        }`}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
      >
        {expanded ? (
          <FolderOpen size={14} className={isSelected ? 'text-primary-600' : 'text-yellow-500'} />
        ) : (
          <Folder size={14} className={isSelected ? 'text-primary-600' : 'text-yellow-500'} />
        )}
        <span className="truncate flex-1">{folder.name}</span>
        {expanded && children.length > 0 && (
          <ChevronRight size={12} className="flex-shrink-0 rotate-90 text-gray-400" />
        )}
      </button>
      {expanded &&
        children.map((child) => (
          <FolderTreeItem
            key={child.id}
            folder={child}
            depth={depth + 1}
            currentFolderId={currentFolderId}
            onSelect={onSelect}
          />
        ))}
    </div>
  );
}

// ─── FileExplorer ─────────────────────────────────────────────────────────────

interface FileExplorerProps {
  rootFolderId?: string;
  embedded?: boolean;
  className?: string;
}

export function FileExplorer({ rootFolderId, embedded, className }: FileExplorerProps) {
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(rootFolderId ?? null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [showDriveOptions, setShowDriveOptions] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewItem, setPreviewItem] = useState<EntityAttachment | null>(null);
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showMovePicker, setShowMovePicker] = useState(false);
  const [editingDocType, setEditingDocType] = useState<{ id: string; value: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { confirm } = useDialog();

  // If rootFolderId changes externally, reset current folder
  useEffect(() => {
    if (rootFolderId !== undefined) setCurrentFolderId(rootFolderId);
  }, [rootFolderId]);

  // Data
  const { data: rootFolders = [], isLoading: rootLoading } = useRootFolders();
  const { data: subFolders = [] } = useDocumentFolders(
    currentFolderId ? { parentId: currentFolderId } : {},
  );
  const { data: breadcrumb = [] } = useFolderBreadcrumb(currentFolderId);
  const { data: attachments = [], isLoading: attLoading } = useAttachmentsByFolder(currentFolderId);

  // Mutations
  const updateFolder = useUpdateDocumentFolder();
  const deleteFolder = useDeleteDocumentFolder();
  const createAttachment = useCreateEntityAttachment();
  const deleteAttachment = useDeleteEntityAttachment();
  const updateAttachment = useUpdateEntityAttachment();
  const bulkMove = useBulkMoveEntityAttachments();
  const { pickAndLink, pickAndDownload, status: driveStatus } = useGoogleDrive();

  // Document type config
  const { data: settingLists = [] } = useAppSettingLists();
  const documentTypeList = settingLists.find((l) => l.listCode === 'document_type');
  const documentTypes =
    (documentTypeList?.items as Array<{ value: string; label: string }> | undefined) ?? [];

  // The folder tree roots: if rootFolderId, show its sub-folders. Otherwise, show global roots.
  const treeRoots = rootFolderId ? subFolders : rootFolders;
  const displayedFolders = currentFolderId ? subFolders : rootFolderId ? [] : rootFolders;

  const isProcessing =
    isUploading ||
    driveStatus === 'downloading' ||
    driveStatus === 'authorizing' ||
    driveStatus === 'picking' ||
    driveStatus === 'loading-sdk';

  const handleUpload = useCallback(
    async (file: File) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setUploadError('Unsupported file type. Allowed: PDF, images, Word, Excel.');
        return;
      }
      if (file.size > MAX_SIZE) {
        setUploadError('File exceeds the 50 MB limit.');
        return;
      }
      setUploadError('');
      setIsUploading(true);
      try {
        const { uploadUrl, s3Key } = await getAttachmentUploadUrl(file.name, file.type);
        await uploadFileToS3(uploadUrl, file);
        const input: CreateEntityAttachmentInput = {
          entityType: 'FOLDER',
          entityId: currentFolderId || 'root',
          fileName: file.name,
          s3Key,
          mimeType: file.type,
          fileSizeBytes: file.size,
          folderId: currentFolderId,
        };
        await createAttachment.mutateAsync(input);
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : 'Upload failed.');
      } finally {
        setIsUploading(false);
      }
    },
    [currentFolderId, createAttachment],
  );

  const handleDriveDownload = useCallback(async () => {
    setShowDriveOptions(false);
    setUploadError('');
    const result = await pickAndDownload();
    if (result) {
      const base = {
        entityType: 'FOLDER',
        entityId: currentFolderId || 'root',
        fileName: result.name,
        mimeType: result.mimeType,
        folderId: currentFolderId,
      };
      const input: CreateEntityAttachmentInput = result.s3Key
        ? { ...base, s3Key: result.s3Key, fileSizeBytes: result.fileSizeBytes }
        : {
            ...base,
            externalUrl: result.externalUrl,
            externalSource: 'GOOGLE_DRIVE',
            externalFileId: result.externalFileId,
          };
      await createAttachment.mutateAsync(input);
    }
  }, [currentFolderId, pickAndDownload, createAttachment]);

  const handleDriveLink = useCallback(async () => {
    setShowDriveOptions(false);
    setUploadError('');
    const result = await pickAndLink();
    if (result) {
      const input: CreateEntityAttachmentInput = {
        entityType: 'FOLDER',
        entityId: currentFolderId || 'root',
        fileName: result.name,
        mimeType: result.mimeType,
        externalUrl: result.url,
        externalSource: 'GOOGLE_DRIVE',
        externalFileId: result.fileId,
        folderId: currentFolderId,
      };
      await createAttachment.mutateAsync(input);
    }
  }, [currentFolderId, pickAndLink, createAttachment]);

  const handleDeleteAttachment = useCallback(
    async (att: EntityAttachment) => {
      const confirmed = await confirm({
        title: 'Remove File',
        message: `Remove "${att.fileName || 'this file'}"?`,
        confirmLabel: 'Remove',
      });
      if (!confirmed) return;
      deleteAttachment.mutate({
        id: att.id,
        entityType: att.entityType,
        entityId: att.entityId,
      });
    },
    [deleteAttachment, confirm],
  );

  const handleDeleteFolder = useCallback(
    async (folder: DocumentFolder) => {
      if (folder.isSystem) return;
      const confirmed = await confirm({
        title: 'Delete Folder',
        message: `Delete folder "${folder.name}"? Files will be moved to parent.`,
        confirmLabel: 'Delete',
      });
      if (!confirmed) return;
      deleteFolder.mutate(folder.id);
      if (currentFolderId === folder.id) setCurrentFolderId(folder.parentId ?? null);
    },
    [deleteFolder, currentFolderId, confirm],
  );

  const handleStartRename = (folder: DocumentFolder) => {
    if (folder.isSystem) return;
    setEditingFolderId(folder.id);
    setEditingName(folder.name);
  };

  const handleConfirmRename = async (id: string) => {
    if (editingName.trim()) {
      await updateFolder.mutateAsync({ id, name: editingName.trim() });
    }
    setEditingFolderId(null);
  };

  const toggleSelectMode = () => {
    setSelectMode((v) => !v);
    setSelectedIds(new Set());
  };

  const toggleSelectFile = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleMoveConfirm = useCallback(
    async (targetFolderId: string | null) => {
      setShowMovePicker(false);
      await bulkMove.mutateAsync({ ids: Array.from(selectedIds), targetFolderId });
      setSelectedIds(new Set());
      setSelectMode(false);
    },
    [selectedIds, bulkMove],
  );

  const handleDocTypeSave = useCallback(
    (id: string, value: string) => {
      updateAttachment.mutate({ id, input: { documentType: value || null } });
      setEditingDocType(null);
    },
    [updateAttachment],
  );

  // ─── Folder Sidebar ─────────────────────────────────────────────────────────

  const sidebarContent = (
    <div className="flex flex-col h-full">
      <div className="px-3 py-3 border-b border-gray-200">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Folders</h3>
      </div>
      <div className="flex-1 overflow-y-auto py-2 px-1">
        {rootLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={20} className="animate-spin text-gray-400" />
          </div>
        ) : treeRoots.length === 0 ? (
          <p className="text-xs text-gray-400 px-3 py-4">No folders</p>
        ) : (
          treeRoots.map((folder) => (
            <FolderTreeItem
              key={folder.id}
              folder={folder}
              depth={0}
              currentFolderId={currentFolderId}
              onSelect={setCurrentFolderId}
            />
          ))
        )}
      </div>
    </div>
  );

  // ─── Breadcrumb ─────────────────────────────────────────────────────────────

  const breadcrumbEl = (
    <nav className="flex items-center gap-1 text-sm text-gray-500 flex-wrap">
      <button
        onClick={() => setCurrentFolderId(rootFolderId ?? null)}
        className="hover:text-primary-600 font-medium"
      >
        {rootFolderId ? 'Folder' : 'All Files'}
      </button>
      {breadcrumb.map((crumb, i) => (
        <span key={crumb.id} className="flex items-center gap-1">
          <ChevronRight size={14} className="text-gray-400" />
          <button
            onClick={() => setCurrentFolderId(crumb.id)}
            className={`hover:text-primary-600 ${i === breadcrumb.length - 1 ? 'text-gray-900 font-semibold' : ''}`}
          >
            {crumb.name}
          </button>
        </span>
      ))}
    </nav>
  );

  // ─── Main Content ────────────────────────────────────────────────────────────

  const isEmpty = displayedFolders.length === 0 && attachments.length === 0 && !attLoading;

  return (
    <div className={`flex h-full ${embedded ? '' : 'min-h-0'} ${className ?? ''}`}>
      {/* Sidebar — desktop always visible */}
      <div className="hidden md:flex flex-col w-56 border-r border-gray-200 bg-gray-50 flex-shrink-0 h-full overflow-hidden">
        {sidebarContent}
      </div>

      {/* Mobile sidebar overlay */}
      {showSidebar && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div className="fixed inset-0 bg-black/40" onClick={() => setShowSidebar(false)} />
          <div className="relative w-64 bg-white h-full shadow-xl flex flex-col overflow-hidden">
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 flex-wrap">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <button
              className="md:hidden p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
              onClick={() => setShowSidebar(true)}
              aria-label="Toggle folder tree"
            >
              <Menu size={18} />
            </button>
            {breadcrumbEl}
          </div>
          {currentFolderId && (
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Select mode toggle */}
              <button
                onClick={toggleSelectMode}
                className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm transition-colors ${
                  selectMode
                    ? 'border-primary-400 bg-primary-50 text-primary-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <CheckSquare size={14} />
                {selectMode ? 'Cancel' : 'Select'}
              </button>

              {/* Upload */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                {isProcessing ? (
                  <Loader2 size={14} className="animate-spin text-primary-600" />
                ) : (
                  <Upload size={14} />
                )}
                Upload
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleUpload(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
              />

              {/* Google Drive */}
              <div className="relative">
                <button
                  onClick={() => setShowDriveOptions((v) => !v)}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <HardDrive size={14} className="text-blue-500" />
                  Drive
                </button>
                {showDriveOptions && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-52">
                    <button
                      onClick={handleDriveDownload}
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2 rounded-t-lg"
                    >
                      <Download size={12} /> Download as PDF to S3
                    </button>
                    <button
                      onClick={handleDriveLink}
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2 rounded-b-lg border-t border-gray-100"
                    >
                      <Link size={12} /> Link only (keep in Drive)
                    </button>
                  </div>
                )}
              </div>

              {/* New Folder */}
              <button
                onClick={() => setShowNewFolder(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <FolderPlus size={14} />
                New Folder
              </button>
            </div>
          )}
        </div>

        {uploadError && (
          <div className="px-4 py-2 bg-red-50 border-b border-red-100">
            <p className="text-xs text-red-600">{uploadError}</p>
          </div>
        )}

        {/* Content grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {!currentFolderId && !rootFolderId ? (
            <div className="text-center py-20">
              <Folder size={48} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-500">
                Select a folder from the sidebar to browse files.
              </p>
            </div>
          ) : isEmpty && !attLoading ? (
            <div className="text-center py-20">
              <FolderOpen size={48} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-500">This folder is empty.</p>
              {currentFolderId && (
                <p className="text-xs text-gray-400 mt-1">
                  Upload files or create sub-folders using the toolbar above.
                </p>
              )}
            </div>
          ) : (
            <>
              {/* Sub-folders grid */}
              {displayedFolders.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Folders
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {displayedFolders.map((folder) => (
                      <div
                        key={folder.id}
                        className="group relative flex flex-col items-center p-3 bg-white border border-gray-200 rounded-xl hover:border-primary-300 hover:shadow-sm transition-all cursor-pointer"
                        onClick={() => setCurrentFolderId(folder.id)}
                      >
                        <FolderOpen size={32} className="text-yellow-400 mb-2" />
                        {editingFolderId === folder.id ? (
                          <div
                            className="flex items-center gap-1 w-full"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              autoFocus
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleConfirmRename(folder.id);
                                if (e.key === 'Escape') setEditingFolderId(null);
                              }}
                              className="text-xs border border-gray-300 rounded px-1 py-0.5 flex-1 min-w-0"
                            />
                            <button
                              onClick={() => handleConfirmRename(folder.id)}
                              className="text-primary-600 hover:text-primary-700"
                            >
                              <Check size={12} />
                            </button>
                          </div>
                        ) : (
                          <p className="text-xs font-medium text-gray-800 text-center truncate w-full">
                            {folder.name}
                          </p>
                        )}
                        {!folder.isSystem && editingFolderId !== folder.id && (
                          <div className="absolute top-1 right-1 hidden group-hover:flex items-center gap-0.5">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartRename(folder);
                              }}
                              className="p-0.5 text-gray-400 hover:text-primary-600 rounded"
                              title="Rename"
                            >
                              <Pencil size={11} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleDeleteFolder(folder);
                              }}
                              className="p-0.5 text-gray-400 hover:text-red-600 rounded"
                              title="Delete"
                            >
                              <Trash2 size={11} />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Files grid */}
              {attLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={24} className="animate-spin text-gray-400" />
                </div>
              ) : attachments.length > 0 ? (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Files
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {attachments.map((att) => {
                      const isSelected = selectedIds.has(att.id);
                      return (
                        <div
                          key={att.id}
                          onClick={selectMode ? () => toggleSelectFile(att.id) : undefined}
                          className={`group flex items-start gap-3 p-3 bg-white border rounded-xl transition-all ${
                            selectMode
                              ? 'cursor-pointer ' +
                                (isSelected
                                  ? 'border-primary-400 bg-primary-50 shadow-sm'
                                  : 'border-gray-200 hover:border-primary-200')
                              : 'border-gray-200 hover:border-primary-300 hover:shadow-sm'
                          }`}
                        >
                          {selectMode && (
                            <div className="flex-shrink-0 mt-0.5">
                              <div
                                className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                                  isSelected
                                    ? 'bg-primary-600 border-primary-600'
                                    : 'border-gray-300 bg-white'
                                }`}
                              >
                                {isSelected && <Check size={10} className="text-white" />}
                              </div>
                            </div>
                          )}
                          {!selectMode && (
                            <div className="flex-shrink-0 mt-0.5">{getFileIcon(att)}</div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-gray-900 truncate">
                              {att.fileName || 'Attachment'}
                            </p>
                            <p className="text-xs text-gray-400">
                              {att.externalSource === 'GOOGLE_DRIVE'
                                ? 'Google Drive'
                                : att.fileSizeBytes
                                  ? formatFileSize(att.fileSizeBytes)
                                  : ''}
                              {att.fileSizeBytes || att.externalSource ? ' · ' : ''}
                              {new Date(att.createdAt).toLocaleDateString()}
                            </p>
                            {att.documentType && (
                              <span className="inline-block mt-0.5 text-[10px] px-1.5 py-0.5 bg-primary-50 text-primary-600 rounded font-medium">
                                {documentTypes.find((t) => t.value === att.documentType)?.label ??
                                  att.documentType}
                              </span>
                            )}
                          </div>
                          {!selectMode && (
                            <div className="flex items-center gap-0.5 flex-shrink-0">
                              {canPreview(att) && (
                                <button
                                  onClick={() => setPreviewItem(att)}
                                  className="p-1 text-gray-400 hover:text-primary-600 rounded"
                                  title="Preview"
                                >
                                  <Eye size={13} />
                                </button>
                              )}
                              {att.downloadUrl && att.externalSource !== 'GOOGLE_DRIVE' && (
                                <a
                                  href={att.downloadUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1 text-gray-400 hover:text-primary-600 rounded"
                                  title="Download"
                                >
                                  <Download size={13} />
                                </a>
                              )}
                              {documentTypes.length > 0 && (
                                <button
                                  onClick={() =>
                                    setEditingDocType({
                                      id: att.id,
                                      value: att.documentType ?? '',
                                    })
                                  }
                                  className={`p-1 rounded ${att.documentType ? 'text-primary-500 hover:text-primary-700' : 'text-gray-400 hover:text-primary-600'}`}
                                  title="Set file type"
                                >
                                  <Tag size={13} />
                                </button>
                              )}
                              <button
                                onClick={() => void handleDeleteAttachment(att)}
                                className="p-1 text-gray-400 hover:text-red-600 rounded"
                                title="Remove"
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* Floating action bar for multi-select */}
      {selectMode && selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 px-4 py-3 bg-gray-900 text-white rounded-2xl shadow-2xl">
          <span className="text-sm font-medium">
            {selectedIds.size} file{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          <button
            onClick={() => setShowMovePicker(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-500 rounded-lg text-sm font-medium transition-colors"
          >
            <Move size={14} />
            Move to…
          </button>
          <button
            onClick={() => {
              setSelectedIds(new Set());
              setSelectMode(false);
            }}
            className="p-1.5 text-gray-400 hover:text-white rounded-lg transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Modals */}
      {showNewFolder && (
        <NewFolderModal parentId={currentFolderId} onClose={() => setShowNewFolder(false)} />
      )}
      {previewItem && (
        <PreviewModal attachment={previewItem} onClose={() => setPreviewItem(null)} />
      )}
      {showMovePicker && (
        <FolderPickerModal
          count={selectedIds.size}
          excludeFolderId={currentFolderId ?? undefined}
          onConfirm={handleMoveConfirm}
          onClose={() => setShowMovePicker(false)}
        />
      )}
      {editingDocType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/40" onClick={() => setEditingDocType(null)} />
          <div className="relative bg-white rounded-xl shadow-xl p-5 w-full max-w-xs mx-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Set File Type</h3>
            <select
              value={editingDocType.value}
              onChange={(e) =>
                setEditingDocType((prev) => prev && { ...prev, value: e.target.value })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 mb-4"
            >
              <option value="">— None —</option>
              {documentTypes.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setEditingDocType(null)}
                className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDocTypeSave(editingDocType.id, editingDocType.value)}
                className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
